import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Dialog, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import "../../styles/sharedStyles.scss";
import Grid from "@mui/material/Grid";
import Clear from "@mui/icons-material/Clear";
import cancelOrderStyles from "./CancelOrderDialog.module.scss";
import subNavStyles from "../../shared/subnav/subNavStyles";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import { withTranslation } from "react-i18next";
import CheckBox from "../../shared/checkBox/CheckBox";
import checkboxStyles from "../../shared/checkBox/CheckBox.module.scss";
import { KeyboardArrowDown } from "@mui/icons-material";
import { CANCELLED } from "../../common/Constants";
import CustomerReservationsClient from "../../shared/clients/CustomerReservationsClient";
import UpperCaseText from "../../utils/UpperCaseText";
import { IS_NA_MARKET } from "../../utils/EmpUtil";

const StyledSelect = styled(Select)({
  "& .MuiSelect-select": {
    width: "100%",
    boxSizing: "border-box",
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 10,
    paddingRight: 10,
    fontSize: 14,
    color: "#4a4a4a",
    lineHeight: 1.57,
    letterSpacing: 1.5,
    fontFamily: "FordAntenna-Regular, sans-serif",
  },
});

const StyledClear = styled(Clear)({
  width: 27,
  height: 27,
  fill: "#102b4e",
  align: "right",
});

const StyledMenuItem = styled(MenuItem)({
  paddingTop: 5,
  paddingBottom: 5,
  paddingLeft: 10,
  paddingRight: 10,
  margin: 0,
  fontSize: 14,
  color: "#102b4e",
  lineHeight: 1.57,
  letterSpacing: 1.5,
  fontFamily: "FordAntenna-Regular, sans-serif",
  "&:hover": {
    backgroundColor: "#102b4e",
    color: "white",
  },
});

const StyledDialog = styled(Dialog)({
  "& .MuiPaper-root": {
    width: 700,
    maxWidth: 700,
  },
});

const StyledGrid = styled(Grid)({
  width: 700,
  maxWidth: 700,
  "&.MuiGrid-container": {
    width: 700,
    maxWidth: 700,
  },
});

function CancelOrderDialog({
                             showDropdown,
                             isRefund,
                             handleConfirm,
                             purchaseRequestCancel,
                             user,
                             orderType,
                             lang,
                             open,
                             onClose,
                             dialogProps,
                             t,
                             is400Error,
                           }) {
  const [dropDownValue, setDropDownValue] = useState("");
  const [checkBox, setCheckBox] = useState(false);
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const [isOtherReasonAvailable, setIsOtherReasonAvailable] = useState(false);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);
  const [reasonsLoadError, setReasonsLoadError] = useState(false);

  const otherReasonCode = "RC_OTHER";
  const otherReasonDisplayText = "Other";
  const cancelOrder = "REQUEST FROM CUSTOMER TO CANCEL ORDER";
  const cancelReservation = "Request from customer to cancel reservation";

  const translations = useMemo(() => ({
    "REQUEST FROM CUSTOMER TO CANCEL RESERVATION":
      "Demande d'annulation de la réservation par le client",
    OTHER: "Autre",
    "UNABLE TO CONTACT CUSTOMER": "Impossible de contacter le client",
    "INABILITY TO COMPLETE PURCHASE": "Incapacité d'effectuer l'achat",
    "PURCHASE OF UNAUTHORIZED RE-SELLER": "Achat d'un revendeur non autorisé",
  }), []);

  const handleConfirmAction = useCallback(() => {
    if (isRefund) {
      handleConfirm();
    } else if (purchaseRequestCancel) {
      handleConfirm(dropDownValue);
    } else if (IS_NA_MARKET(user.market)) {
      handleConfirm(CANCELLED, dropDownValue);
    } else {
      handleConfirm(CANCELLED, null, dropDownValue);
    }
  }, [isRefund, handleConfirm, purchaseRequestCancel, dropDownValue, user.market]);

  const changeConfirmButtonState = useCallback(() => {
    setCheckBox(prev => !prev);
  }, []);

  const getStyledMenuItem = useCallback((key, message) => {
    return (
      <StyledMenuItem
        data-testid={key}
        value={IS_NA_MARKET(user.market) ? message : key}
        key={key}
      >
        <div className={cancelOrderStyles.menuItems}>{message}</div>
      </StyledMenuItem>
    );
  }, [user.market]);

  const getMenuItem = useCallback((key, message) => {
    if (
      UpperCaseText(orderType) === "RESERVATION" &&
      UpperCaseText(message) === cancelOrder
    ) {
      message = cancelReservation;
    }
    if (
      UpperCaseText(orderType) === "RESERVATION" &&
      localStorage.getItem("lang") === "fr_CA" &&
      translations[UpperCaseText(message)]
    ) {
      message = translations[UpperCaseText(message)];
    }

    if (UpperCaseText(key) === otherReasonCode) {
      return null;
    }

    return getStyledMenuItem(key, message);
  }, [orderType, translations, getStyledMenuItem]);

  const getCancelOrderReasons = useCallback(() => {
    setIsLoadingReasons(true);
    setReasonsLoadError(false);
    setCancellationReasons([]);
    setIsOtherReasonAvailable(false);
    setDropDownValue("");

    return CustomerReservationsClient.getCancelOrderReasons(
      lang,
      purchaseRequestCancel,
      false,
      !purchaseRequestCancel
    ).then((response) => {
      const reasons = response && response.cancellationReasons;
      if (
        !Array.isArray(reasons) ||
        reasons.some(
          (reason) =>
            !reason ||
            typeof reason.code !== "string" ||
            typeof reason.displayText !== "string"
        )
      ) {
        throw new Error("Invalid cancellation reasons response");
      }

      setCancellationReasons(reasons);
      setIsOtherReasonAvailable(
        reasons.some(
          (reason) => UpperCaseText(reason.code) === otherReasonCode
        )
      );
    }).catch(() => {
      setCancellationReasons([]);
      setIsOtherReasonAvailable(false);
      setReasonsLoadError(true);
    }).finally(() => {
      setIsLoadingReasons(false);
    });
  }, [lang, purchaseRequestCancel]);

  const updateDropdown = useCallback((event) => {
    setDropDownValue(event.target.value);
  }, []);

  useEffect(() => {
    if (showDropdown) {
      getCancelOrderReasons();
    }
  }, [showDropdown, getCancelOrderReasons]);

  return (
    <StyledDialog
      open={open}
      className={cancelOrderStyles.dialog}
      onClose={onClose}
    >
      <StyledGrid
        container
        data-testid="cancelOrderDialog"
        className={cancelOrderStyles.dialogContainer}
        justifyContent="center"
      >
        <Grid item md={12} className={cancelOrderStyles.cancel}>
          <div>
            <button data-testid="closeDialog" onClick={onClose}>
              <StyledClear />
            </button>
          </div>
        </Grid>

        <Grid item md={9}>
          <div className={cancelOrderStyles.dialogTitle}>
            <span className={cancelOrderStyles.warning}></span>
            <div>{dialogProps.title}</div>
          </div>
        </Grid>

        <Grid item md={9}>
          <div className={cancelOrderStyles.dialogDescription}>
            {dialogProps.body}
          </div>
        </Grid>

        {showDropdown && (
          <Grid item md={9} className={cancelOrderStyles.dropdown}>
            <div className={cancelOrderStyles.dropdownDiv}>
              <div className={cancelOrderStyles.cancelReason}>
                {t("CustomerOrderDetails.reasonForCancelling")}
              </div>
              <FormControl id="cancelOrderDropDown" fullWidth={true}>
                {!isLoadingReasons &&
                  (reasonsLoadError || cancellationReasons.length === 0 ? (
                    <>
                      <div className={cancelOrderStyles.errorText}>
                        {t(
                          reasonsLoadError
                            ? "CustomerOrderDetails.cancellationReasonsLoadError"
                            : "CustomerOrderDetails.cancellationReasonsEmpty"
                        )}
                      </div>
                      <Button
                        type="button"
                        data-testid="retryCancelReasons"
                        onClick={getCancelOrderReasons}
                      >
                        {t("CustomerOrderDetails.retry")}
                      </Button>
                    </>
                  ) : (
                    <StyledSelect
                      IconComponent={() => (
                        <KeyboardArrowDown style={subNavStyles.icon} />
                      )}
                      name="dropdown"
                      data-testid="dropdown"
                      className={cancelOrderStyles.dropdownCancelOrder}
                      value={dropDownValue}
                      onChange={updateDropdown}
                    >
                      <StyledMenuItem value="" disabled>
                        <div>
                          {t("CustomerOrderDetails.pleaseSelect")}
                        </div>
                      </StyledMenuItem>
                      {cancellationReasons.map((item) =>
                        getMenuItem(item.code, item.displayText)
                      )}
                      {isOtherReasonAvailable &&
                        getStyledMenuItem(
                          otherReasonCode,
                          otherReasonDisplayText
                        )}
                    </StyledSelect>
                  ))}
              </FormControl>
            </div>
          </Grid>
        )}

        <Grid item md={9}>
          <div className={cancelOrderStyles.checkbox}>
            <CheckBox
              boldCheck={true}
              textClass={checkboxStyles.cancelLabel}
              checked={checkBox}
              isDisabled={false}
              name="chkBxConfirm"
              id="chkBxConfirm"
              label={dialogProps.checkbox}
              labelList={dialogProps.checkboxList}
              onChange={changeConfirmButtonState}
            />
          </div>
        </Grid>

        <Grid item md={9}>
          <div className={cancelOrderStyles.dialogActions}>
            <Button
              className={cancelOrderStyles.goBack}
              name="cancel"
              data-testid="cancel"
              onClick={onClose}
            >
              {dialogProps.cancel}
            </Button>
            <Button
              className={cancelOrderStyles.confirmShared}
              name="confirmShared"
              data-testid="confirmShared"
              disabled={
                checkBox === false ||
                (showDropdown && dropDownValue === undefined) ||
                (showDropdown && dropDownValue === "")
              }
              onClick={handleConfirmAction}
            >
              {dialogProps.confirm}
            </Button>
          </div>
          {is400Error && (
            <div className={cancelOrderStyles.errorText}>
              {t("CustomerOrderDetails.cancellationError")}
            </div>
          )}
        </Grid>
      </StyledGrid>
    </StyledDialog>
  );
}

export default withTranslation("emp")(CancelOrderDialog);
